import React, { useCallback, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import {
  getChangeOrderData,
  getChangeOrderStatuses,
} from "selectors/changeOrderSelectors";
import {
  updateChangeOrderStatusAction,
  deleteChangeOrderAction,
  fetchChangeOrderByIdAction,
  fetchChangeOrderStatusAction,
  clearChangeOrderData,
} from "../../actions/change_orderActions";
import AppRoleContext from "context/AppRoleContext";
import { WithTypeSelection as AddChangeOrder } from "./AddChangeOrder";
import { getMomentDate, formatNumber } from "core/utils/localizationUtils";
import RenderDataFields from "components/DataFields/RenderDataFields";
import DataGridTable from "components/Table/DataGrid";
import {
  Box,
  Hidden,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  Approval,
  Close,
  DeleteOutlined,
  EditOutlined,
} from "@mui/icons-material";
import HiddenOn from "components/Wrappers/HiddenOn";
import { isHomeOwner, isProjectLimitedView } from "core/utils/roleUtils";

export const ViewChangeOrder = (props) => {
  const { onClose, type, changeOrder = {}, projectData = {}, propertyId } = props;
  const { changeOrderId } = changeOrder;
  const appRoleDetails = useContext(AppRoleContext);

  const dispatch = useDispatch();
  const changeOrderData = useSelector(getChangeOrderData);
  const changeOrderStatuses = useSelector(getChangeOrderStatuses);

  const changeOrderStatusOptions = changeOrderStatuses.map((status) => {
    return {
      key: status.changeOrderStatusId,
      value: status.statusName,
      text: status.statusName,
      data: status,
    };
  });

  const [editChangeOrder, setEditChangeOrder] = React.useState(false);

  const fetchChangeOrder = useCallback((response = null) => {
    dispatch(fetchChangeOrderByIdAction(changeOrderId));
    dispatch(
      fetchChangeOrderStatusAction(
        response ? response?.changeOrderStatusId : changeOrder?.changeOrderStatusId,
        propertyId
      )
    );
  }, [
    changeOrderId,
    changeOrder.changeOrderStatusId,
    dispatch,
    propertyId,
  ]);

  useEffect(() => {
    fetchChangeOrder();
  }, [fetchChangeOrder]);

  if (editChangeOrder) {
    return (
      <AddChangeOrder
        changeOrderId={changeOrderId}
        type={type}
        selectedChangeOrder={{
          changeOrderId,
          data: changeOrderData,
        }}
        {...props}
        onClose={() => {
          setEditChangeOrder(false);
          dispatch(clearChangeOrderData());
          fetchChangeOrder();
        }}
      />
    );
  }
  const projectUserRole = projectData?.projUserRole;

  const isNotNew = (status) => {
    const currentStatus = get(
      changeOrderData,
      "changeOrderStatusReqResp.statusName",
      ""
    );
    return currentStatus.toLowerCase() !== "new";
  };

  return (
    <div className="p2 p-md-3">
      <div className="d-flex justify-content-between">
        <Typography className="h1 mt-3">
          {changeOrderData?.changeOrderNumber}
        </Typography>
        <IconButton>
          <Close
            onClick={() => {
              if (onClose) {
                onClose();
              }
              dispatch(clearChangeOrderData());
            }}
          />
        </IconButton>
      </div>
      <HiddenOn
        isHidden={isProjectLimitedView(appRoleDetails, projectUserRole)}
      >
        <div className="d-flex justify-content-end me-md-4 me-0">
          <Select
            displayEmpty
            disabled={
              changeOrderData?.changeOrderStatusReqResp?.statusName ===
              "Completed"
            }
            className="primaryButton col-7"
            renderValue={() => (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Approval sx={{ mr: 1, fontSize: 14 }} />
                <Typography
                  variant="body2"
                  className={"body1 ms-2 me-2"}
                  style={{ color: "white" }}
                >
                  Change Status
                </Typography>
              </Box>
            )}
            onChange={(e, ...rest) => {
              // Handle status change logic here

              const selectedOption = e.target.value;
              dispatch(
                updateChangeOrderStatusAction({
                  newStatus: selectedOption,
                  currentChangeOrderData: changeOrderData,
                  onSuccess:(response) => {
                    fetchChangeOrder(response);
                  },
                })
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 200 },
              },
            }}
          >
            {changeOrderStatusOptions.map((option) => (
              <MenuItem key={option.key} value={option}>
                {option.text}
              </MenuItem>
            ))}
          </Select>

          <HiddenOn
            isHidden={isNotNew(changeOrderData) || isHomeOwner(appRoleDetails)}
          >
            <button
              className="secondaryButton icon-btn ms-md-2 ms-1"
              // style={{ width: "3rem" }}
              onClick={() => {
                setEditChangeOrder(true);
              }}
              style={{ width: "7rem" }}
            >
              Edit Order
              <Hidden smDown>
                <EditOutlined style={{ fontSize: "14px" }} />
              </Hidden>
            </button>
          </HiddenOn>
          <button
            className="secondaryButton delete-btn ms-md-2 ms-1 me-sm-1"
            // style={{ width: "auto" }}
            onClick={() => {
              dispatch(
                deleteChangeOrderAction(changeOrderId, () => {
                  onClose();
                })
              );
            }}
            style={{ width: "auto" }}
          >
            Delete
            <Hidden smDown>
              <DeleteOutlined style={{ fontSize: "14px" }} />
            </Hidden>
          </button>
        </div>
      </HiddenOn>
      <div>
        <RenderChangeOrderFields changeOrderData={changeOrderData || {}} />
      </div>
    </div>
  );
};

export const RenderChangeOrderFields = ({ changeOrderData = {} }) => {
  const { changeOrderItemsList = [] } = changeOrderData;

  const displayedFields = [
    {
      key: "changeOrderNumber",
      label: "Change Order Number",
      dataPath: "changeOrderNumber",
    },
    {
      key: "changeOrderDate",
      label: "Change Order Date",
      dataPath: "changeOrderDate",
      formatter: (value) => getMomentDate(value),
    },
    {
      key: "changeOrderStatusReqResp.statusName",
      label: "Status",
      dataPath: "changeOrderStatusReqResp.statusName",
      formatter: (value) => (value != null ? value : "N/A"),
    },
    {
      key: "initiatedBy",
      label: "Order Raised By",
      dataPath: "initiatedBy",
    },
    {
      key: "clientName",
      label: "Client Name",
      dataPath: "clientName",
    },
    {
      key: "clientEmail",
      label: "Client Email",
      dataPath: "clientEmail",
    },
    {
      key: "clientMobile",
      label: "Client Mobile",
      dataPath: "clientMobile",
    },
    {
      key: "changesRequired",
      label: "Changes Required",
      dataPath: "changesRequired",
    },
    {
      key: "reasonForChange",
      label: "Reason For Change",
      dataPath: "reasonForChange",
    },
    {
      key: "originalContractCost",
      label: "Original Contract Cost",
      dataPath: "originalContractCost",
      formatter: (value) => formatNumber(value),
    },
    {
      key: "estimatedChangeCost",
      label: "Estimated Change Cost",
      dataPath: "estimatedChangeCost",
      formatter: (value) => formatNumber(value),
    },
    {
      key: "originalCompletionDate",
      label: "Original Completion Date",
      dataPath: "originalCompletionDate",
      formatter: (value) => getMomentDate(value),
    },
    {
      key: "revisedCompletionDate",
      label: "Revised Completion Date",
      dataPath: "revisedCompletionDate",
      formatter: (value) => getMomentDate(value),
    },
  ];

  return (
    <div>
      <RenderDataFields data={changeOrderData} fields={displayedFields} />
      {changeOrderItemsList.length > 0 && (
        <div>
          <DataGridTable
            columns={[
              { field: "itemName", headerName: "Item Name", flex: 1 },
              { field: "quantity", headerName: "Quantity", flex: 1 },
              { field: "unit", headerName: "Unit", flex: 1 },
              {
                field: "unitPrice",
                headerName: "Unit Price",
                flex: 1,
                valueFormatter: (value) => formatNumber(value),
              },
              {
                field: "amount",
                headerName: "Amount",
                flex: 1,
                valueFormatter: (value) => formatNumber(value),
              },
            ]}
            defineRowId={(row) => row.changeOrderItemId}
            data={changeOrderItemsList || []}
            pagination={false}
            checkboxSelection={false}
            showToolbar={false}
          />
        </div>
      )}
    </div>
  );
};

export default ViewChangeOrder;
